import * as Yup from "yup";
import { useState } from "react";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { Col, Row } from "react-bootstrap";
import styles from "../ReportIssues.module.scss";
import Button from "../../../../../common/Button/Button";
import Upload from "../../../../../common/Formik/Upload/Upload";
import Textarea from "../../../../../common/Formik/Textarea/Textarea";
import ErrorText from "../../../../../common/Formik/ErrorText/ErrorText";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";
import { reportIssueAction } from "../../../../../../features/user/userSlice";

const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const OtherIssues = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const MAX_FILE_SIZE_MB = 2;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "application/pdf",
  ]

  const validationSchema = Yup.object({
    transactionId: Yup.string()
      .trim()
      .required("Transaction ID is required")
      .min(6, "Transaction ID must be at least 6 characters")
      .max(50, "Transaction ID must not exceed 50 characters"),

    amount: Yup.number()
      .typeError("Amount must be a valid number")
      .required("Amount is required")
      .positive("Amount must be greater than 0")
      .max(10000000, "Amount is too large"),

    description: Yup.string()
      .trim()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must not exceed 500 characters"),

    profile_img: Yup.mixed()
      .nullable()
      .test(
        "fileExists",
        "Invalid file",
        (file) => !file || file instanceof File
      )

      //  Size validation
      .test(
        "fileSize",
        `File size must be less than ${MAX_FILE_SIZE_MB} MB`,
        (file) => !file || file.size <= MAX_FILE_SIZE
      )

      //  Type validation (MIME)
      .test(
        "fileType",
        "Only JPG, JPEG, PNG, or PDF files are allowed",
        (file) => !file || SUPPORTED_FORMATS.includes(file.type)
      )

      //  Extension safety (extra layer)
      .test(
        "fileExtension",
        "Invalid file extension",
        (file) => {
          if (!file) return true;
          const ext = file.name?.split(".").pop()?.toLowerCase();
          return ["jpg", "jpeg", "png", "pdf"].includes(ext);
        }
      ),

  });

  const reportSubmit = async (values, resetForm) => {
    try {
      setLoading(true);

      let attachments;

      if (values.profile_img) {
        const base64 = await blobToBase64(values.profile_img);
        attachments = [base64];
      }

      const payload = {
        dispute_type: "other",
        amount: Number(values.amount),
        description: values.description,
        transaction_id: values.transactionId,
        ...(attachments && { attachments }),
      };

      const res = await dispatch(reportIssueAction(payload));

      if (res?.status === 200 || res?.status === 201) {
        resetForm();
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className={styles.report_issues_content}>
      <h5>Report Other Issue</h5>

      <div className={styles.report_issues_content_form}>
        <Formik
          initialValues={{
            amount: "",
            description: "",
            transactionId: "",
            profile_img: null,

          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            reportSubmit(values, resetForm)
            const payload = { ...values };
          }}
        >
          {(formik) => (
            <Form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <Row>
                <Col lg={4} md={6}>
                  <InputFormik
                    name="transactionId"
                    label="Transaction ID"
                    placeholder="Enter if applicable"
                    className={styles.input}
                    {...formik.getFieldProps("transactionId")}
                    formik={formik}
                  />
                </Col>

                <Col lg={4} md={6}>
                  <InputFormik
                    name="amount"
                    label="Amount"
                    placeholder="$0.00"
                    className={styles.input}
                    {...formik.getFieldProps("amount")}
                    formik={formik}
                  />
                </Col>

                <Col lg={6}>
                  <Textarea
                    name="description"
                    label="Description"
                    placeholder="Please describe your issue in detail..."
                    formik={formik}
                    value={formik.values.description}
                    className={styles.form_textarea}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Col>

                <Col lg={6}>
                  <Upload
                    name="profile_img"
                    label="Upload receipt"
                    formik={formik}
                    image={formik.values.profile_img}
                    setBlobImage={(file) => {
                      formik.setFieldValue("profile_img", file);
                      formik.setFieldTouched("profile_img", true);
                    }}
                  />

                  {formik.touched.profile_img && formik.errors.profile_img && (
                    <ErrorText>{formik.errors.profile_img}</ErrorText>
                  )}

                </Col>
              </Row>
              <div className={styles.form_btns}>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  variant="bordered_grey"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  disabled={loading}
                  onClick={(e) => {
                    formik.handleSubmit(e);
                  }}
                >
                  {loading ? "Submitting..." : "Submit Issue"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default OtherIssues;
